import { useEffect, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import { API } from "@/lib/auth";

export default function Gallery() {
  const [data, setData] = useState({ items: [], tags: [] });
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const filtered =
    filter === "All"
      ? data.items
      : data.items.filter((i) => (i.tags || []).includes(filter));

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div data-testid="page-gallery" className="pt-32 pb-24">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
        <div className="grid gap-6 lg:grid-cols-12 items-end mb-10">
          <div className="lg:col-span-8">
            <div className="overline">Gallery</div>
            <h1 className="font-display text-5xl sm:text-7xl mt-4 tracking-tight text-balance">
              The <span className="italic font-light">craft, up close.</span>
            </h1>
            <p className="mt-6 max-w-xl text-[hsl(var(--onyx)/0.65)]">
              A curated look at colour work, extension transformations, hair-loss journeys and the Academy floor.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right text-xs uppercase tracking-widest text-[hsl(var(--onyx)/0.5)]">
            {data.items.length} pieces · Tap to enlarge
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {["All", ...data.tags].map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              data-testid={`gallery-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm transition-all duration-300 ${
                filter === t
                  ? "bg-[hsl(var(--onyx))] text-[hsl(var(--cream))]"
                  : "bg-white border border-[hsl(var(--onyx)/0.1)] text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 text-center text-[hsl(var(--onyx)/0.5)]">Loading…</div>
        ) : (
          <Stagger className="grid gap-4 sm:gap-5 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filtered.map((it, i) => (
              <Item key={it.id || i}>
                <button
                  onClick={() => setLightbox(it)}
                  data-testid={`gallery-item-${i}`}
                  className={`block w-full ${
                    i % 5 === 0 ? "aspect-[3/4]" : i % 5 === 2 ? "aspect-square" : "aspect-[4/5]"
                  } rounded-3xl overflow-hidden hover-zoom bg-blush`}
                >
                  <img
                    src={it.image_url}
                    alt={it.caption || ""}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              </Item>
            ))}
          </Stagger>
        )}

        {!loading && filtered.length === 0 && (
          <div className="py-24 text-center text-[hsl(var(--onyx)/0.5)]">
            No pieces in this category yet.
          </div>
        )}
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 backdrop-blur"
        >
          <button
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center backdrop-blur"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <img
            src={lightbox.image_url}
            alt={lightbox.caption || ""}
            className="max-h-[85vh] max-w-full rounded-3xl object-contain"
          />
          {lightbox.caption && (
            <div className="absolute bottom-8 left-8 right-8 text-center text-white font-editorial text-xl">
              {lightbox.caption}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
